import { BaseApiService } from './BaseApiService';

export interface FilterOption {
  id: string;
  name: string;
}

export interface ComparisonSummary {
  revenue: number;
  qty: number;
  active_customers: number;
  orders: number;
}

export interface ComparisonRow {
  row_key: string;
  row_label: string;
  revenue: number;
  qty: number;
  avg_sale: number;
  min_sale: number;
  max_sale: number;
  avg_price: number;
  min_price: number;
  max_price: number;
}

export interface ComparisonWidget {
  group_key: string;
  group_label: string;
  rows: ComparisonRow[];
}

export type GroupByDimension =
  | 'product'
  | 'customer'
  | 'brand'
  | 'category'
  | 'sales_rep'
  | 'territory';

export interface ComparisonFilters {
  brands?: string[];
  categories?: string[];
  products?: string[];
  customers?: string[];
  sales_reps?: string[];
  territories?: string[];
  start_date?: string;
  end_date?: string;
}

class ComparisonDashboardService extends BaseApiService {
  private basePath = '/reports/comparison-dashboard';

  private filtersToParams(filters: ComparisonFilters): Record<string, any> {
    const params: Record<string, any> = {};
    if (filters.brands?.length) params.brands = filters.brands.join(',');
    if (filters.categories?.length) params.categories = filters.categories.join(',');
    if (filters.products?.length) params.products = filters.products.join(',');
    if (filters.customers?.length) params.customers = filters.customers.join(',');
    if (filters.sales_reps?.length) params.sales_reps = filters.sales_reps.join(',');
    if (filters.territories?.length) params.territories = filters.territories.join(',');
    if (filters.start_date) params.start_date = filters.start_date;
    if (filters.end_date) params.end_date = filters.end_date;
    return params;
  }

  async getFilterOptions(dimension: string, search: string): Promise<FilterOption[]> {
    const response = await this.get<{ success: boolean; data: FilterOption[] }>(
      `${this.basePath}/filter-options`,
      { dimension, search }
    );
    return response.data;
  }

  async getSummary(filters: ComparisonFilters): Promise<ComparisonSummary> {
    const params = this.filtersToParams(filters);
    const response = await this.get<{ success: boolean; data: ComparisonSummary }>(
      `${this.basePath}/summary`,
      params
    );
    return response.data;
  }

  async getComparisonData(
    filters: ComparisonFilters,
    group_by: GroupByDimension
  ): Promise<ComparisonWidget[]> {
    const params = { ...this.filtersToParams(filters), group_by };
    const response = await this.get<{ success: boolean; data: ComparisonWidget[] }>(
      `${this.basePath}/data`,
      params
    );
    return response.data;
  }
}

export const comparisonDashboardService = new ComparisonDashboardService();
